'use strict';

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = initProductFaq;
// PDP FAQ accordion (snippets/product-faq.liquid). Spec: docs/specs/pdp-faq.md
//
// The markup is native <details>/<summary>, so it already opens, closes and
// tabs with no JS, and find-in-page can expand it. This only animates the
// height with the global GSAP (theme.liquid loads it, deferred, before this
// bundle) and keeps single mode to one open item. Without window.gsap, or
// under prefers-reduced-motion, it toggles instantly.
//
// CSS reads the visual state from `[open]` plus `.is-closing`, which is added at
// click time so the icon flips at once while the drawer is still closing.

var REDUCED_MOTION = '(prefers-reduced-motion: reduce)';
var DURATION = 0.45;

// The timeline currently animating each item. Present means "ours is in flight".
var motion = new WeakMap();
// The open state our own code last set, to tell a native toggle from the event our change queues.
var known = new WeakMap();
var refreshTimer = 0;

function prefersReducedMotion() {
  return typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION).matches;
}

// Heights changed: tell ScrollTrigger (debounced) and Lenis, which runs site-wide.
function layoutChanged() {
  var trigger = window.ScrollTrigger;
  if (trigger && typeof trigger.getAll === 'function' && trigger.getAll().length) {
    window.clearTimeout(refreshTimer);
    refreshTimer = window.setTimeout(function () {
      return trigger.refresh();
    }, 150);
  }
  if (window.lenis && typeof window.lenis.resize === 'function') {
    window.lenis.resize();
  }
}

function parts(item) {
  return {
    drawer: item.querySelector('.pdp-faq__drawer'),
    body: item.querySelector('.pdp-faq__body')
  };
}

// Visually open: `open`, and not on its way shut.
function isOpen(item) {
  return item.open && !item.classList.contains('is-closing');
}

function killMotion(item) {
  var tl = motion.get(item);
  if (tl) {
    tl.kill();
    motion.delete(item);
  }

  var _parts = parts(item),
      drawer = _parts.drawer,
      body = _parts.body;

  if (window.gsap && drawer && body) {
    window.gsap.killTweensOf([drawer, body]);
  }
}

// Remove everything the animation wrote, so the item is back to what the CSS says.
function clearMotion(item) {
  var _parts2 = parts(item),
      drawer = _parts2.drawer,
      body = _parts2.body;

  if (!drawer || !body) {
    return;
  }
  if (window.gsap) {
    window.gsap.set(drawer, { clearProps: 'height,overflow' });
    window.gsap.set(body, { clearProps: 'transform,opacity' });
  }
  drawer.style.removeProperty('height');
  drawer.style.removeProperty('overflow');
  body.style.removeProperty('transform');
  body.style.removeProperty('opacity');
}

// Back to the plain <details> state, whatever was in flight.
function settle(item) {
  killMotion(item);
  clearMotion(item);
  item.classList.remove('is-closing');
}

function setOpen(item, open) {
  var gsap = window.gsap;

  var _parts3 = parts(item),
      drawer = _parts3.drawer,
      body = _parts3.body;

  if (!(gsap && drawer && body) || prefersReducedMotion()) {
    settle(item);
    item.open = open;
    known.set(item, open);
    layoutChanged();
    return;
  }

  // Interrupted mid-flight: carry on from where the drawer is, never from a jump.
  var midway = motion.has(item) && drawer.style.height !== '';
  killMotion(item);

  if (open) {
    item.classList.remove('is-closing');
    item.open = true;
    known.set(item, true);
    if (!midway) {
      gsap.set(drawer, { height: 0 });
    }
    drawer.style.overflow = 'hidden';
    var _tl = gsap.timeline({
      onComplete: function onComplete() {
        motion.delete(item);
        clearMotion(item);
        layoutChanged();
      }
    });
    _tl.to(drawer, { height: 'auto', duration: DURATION, ease: 'power3.out' }, 0);
    if (midway) {
      _tl.to(body, { y: 0, opacity: 1, duration: DURATION * 0.8, ease: 'power2.out' }, 0);
    } else {
      _tl.fromTo(body, { y: -8, opacity: 0 }, { y: 0, opacity: 1, duration: DURATION * 0.8, ease: 'power2.out' }, DURATION * 0.15);
    }
    motion.set(item, _tl);
    return;
  }

  item.classList.add('is-closing');
  gsap.set(drawer, { height: drawer.offsetHeight });
  drawer.style.overflow = 'hidden';
  var tl = gsap.timeline({
    onComplete: function onComplete() {
      motion.delete(item);
      item.open = false;
      known.set(item, false);
      clearMotion(item);
      item.classList.remove('is-closing');
      layoutChanged();
    }
  });
  tl.to(drawer, { height: 0, duration: DURATION * 0.85, ease: 'power3.inOut' }, 0);
  tl.to(body, { opacity: 0, y: -4, duration: DURATION * 0.4, ease: 'power2.in' }, 0);
  motion.set(item, tl);
}

function initFaq(section) {
  if (section.dataset.pdpFaqReady === 'true') {
    return;
  }
  section.dataset.pdpFaqReady = 'true';

  var items = Array.from(section.querySelectorAll('details.pdp-faq__item'));

  items.forEach(function (item) {
    known.set(item, item.open);
    // An item that is open must never carry the height of an old close.
    if (item.open) {
      clearMotion(item);
    }
    // Native toggles (find-in-page, or `open` set by other code) land here. So does
    // the event our own change queues, but that one matches what we last set, or
    // our tween is still in flight, and needs nothing.
    item.addEventListener('toggle', function () {
      if (motion.has(item) || known.get(item) === item.open) {
        return;
      }
      known.set(item, item.open);
      settle(item);
      layoutChanged();
    });
  });

  section.addEventListener('click', function (event) {
    var summary = event.target.closest && event.target.closest('summary');
    if (!summary || !section.contains(summary)) {
      return;
    }
    var item = summary.parentElement;
    if (!item || item.tagName !== 'DETAILS') {
      return;
    }
    event.preventDefault();

    var open = !isOpen(item);
    // data-mode is read per click, so a section can switch between single and multi.
    if (open && section.dataset.mode !== 'multi') {
      items.forEach(function (other) {
        if (other !== item && isOpen(other)) {
          setOpen(other, false);
        }
      });
    }
    setOpen(item, open);
  });
}

function run() {
  var sections = document.querySelectorAll('[data-pdp-faq]');
  if (!sections.length) {
    return;
  }
  sections.forEach(initFaq);
}

// Safe to call more than once: a section binds its listeners a single time.
function initProductFaq() {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }
}